import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { BASE_URL } from '../../apiConfig';
import { useAuth } from "../../context/AuthProvider";
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const fetchData = async (url, options) => {
  try {
    const response = await fetch(url, options);
    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat();
          throw new Error(JSON.stringify({ message: errorData.message || 'Validation failed', errors: errorData.errors }));
        }
        throw new Error(errorData.message || 'An unknown error occurred');
      } else {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }
    }

    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    return null;
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

const baseUrlNormalized =  BASE_URL;

export default function UpdateCategoryForm({ category }) {
  const { token } = useAuth();
  
  const [formData, setFormData] = useState({
    id: category?.id || null,
    title: category?.title || '',
    slug: category?.slug || '',
    image: null, // This will hold the NEW File object if uploaded
  });

  const [imagePreview, setImagePreview] = useState(() => {
    if (category?.image) {
      return `${baseUrlNormalized}/storage/${category.image}`;
    }
    return ''; // No initial image
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);


  useEffect(() => {
    if (category) {
      setFormData({
        id: category.id || null,
        title: category.title || '',
        slug: category.slug || '',
        image: null,
      });

      const newImageUrl = category.image
        ? `${baseUrlNormalized}/storage/${category.image}`
        : '';
      setImagePreview(newImageUrl);
      setErrors({});
    }
  }, [category]); 

  const generateSlug = useCallback((title) => {
    return title
      .toLowerCase()
      .normalize('NFD') // Normalize diacritics
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9 -]/g, '') // Remove invalid chars
      .trim() // Trim whitespace from both ends
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/-+/g, '-'); // Replace multiple - with single -
  }, []);

  const handleTitleChange = useCallback((e) => {
    const title = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title,
      slug: generateSlug(title),
    }));
    if (errors.title) {
      setErrors((prevErrors) => ({ ...prevErrors, title: undefined })); // Clear specific error
    }
  }, [generateSlug, errors]);

  const handleSlugChange = useCallback((e) => {
    const slug = generateSlug(e.target.value); // Always sanitize slug input
    setFormData((prev) => ({ ...prev, slug }));
    if (errors.slug) {
      setErrors((prevErrors) => ({ ...prevErrors, slug: undefined }));
    }
  }, [generateSlug, errors]);

  const handleImageUpload = useCallback((file) => {
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        // If invalid file, revert to previous image if available, otherwise clear.
        const prevImageUrl = category?.image ? `${baseUrlNormalized}/storage/${category.image}` : '';
        setImagePreview(prevImageUrl); // Revert preview to original or empty
        setFormData((prev) => ({ ...prev, image: null })); // Do not store invalid file
        if (fileInputRef.current) fileInputRef.current.value = ''; // Clear file input
        setErrors((prevErrors) => ({ ...prevErrors, image: 'Le fichier téléchargé doit être une image (PNG, JPG, GIF, WebP).' }));
        toast.error('Veuillez télécharger une image valide (PNG, JPG, GIF, WebP).');
        return;
      }

      const MAX_FILE_SIZE_MB = 2;
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        const prevImageUrl = category?.image ? `${baseUrlNormalized}/storage/${category.image}` : '';
        setImagePreview(prevImageUrl); // Revert preview to original or empty
        setFormData((prev) => ({ ...prev, image: null })); // Do not store large file
        if (fileInputRef.current) fileInputRef.current.value = ''; // Clear file input
        setErrors((prevErrors) => ({ ...prevErrors, image: `La taille de l'image ne doit pas dépasser ${MAX_FILE_SIZE_MB} Mo.` }));
        toast.error(`La taille de l'image ne doit pas dépasser ${MAX_FILE_SIZE_MB} Mo.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result); // Set new image preview from FileReader
        setFormData((prev) => ({ ...prev, image: file })); // Store the actual File object
      };
      reader.readAsDataURL(file);
      if (errors.image) {
        setErrors((prevErrors) => ({ ...prevErrors, image: undefined }));
      }
    } else {
      const currentCategoryImageUrl = category?.image
        ? `${baseUrlNormalized}/storage/${category.image}`
        : '';
      setImagePreview(currentCategoryImageUrl); // Revert to previous image or empty string
      setFormData((prev) => ({ ...prev, image: null })); // Ensure image is null if no file selected
    }
  }, [errors, category?.image]); // Depend on category.image for fallback

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      e.currentTarget.classList.add('border-[var(--color-primary-500)]', 'bg-[var(--color-primary-50)]');
      e.currentTarget.classList.remove('border-gray-300', 'hover:border-[var(--color-primary-500)]', 'hover:bg-gray-50');
    } else if (e.type === 'dragleave' || e.type === 'drop') {
      e.currentTarget.classList.remove('border-[var(--color-primary-500)]', 'bg-[var(--color-primary-50)]');
      e.currentTarget.classList.add('border-gray-300', 'hover:border-[var(--color-primary-500)]', 'hover:bg-gray-50');
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      handleDrag(e); // Reset drag styles
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleImageUpload(e.dataTransfer.files[0]);
      }
    },
    [handleImageUpload, handleDrag]
  );

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre de la catégorie est requis.';
    } else if (formData.title.length < 3) {
      newErrors.title = 'Le titre doit contenir au moins 3 caractères.';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = "Le slug d'URL est requis.";
    } else if (formData.slug.length < 3) {
      newErrors.slug = 'Le slug doit contenir au moins 3 caractères.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire.');
      return;
    }

    if (!formData.id) {
      toast.error('Impossible de mettre à jour : l\'ID de la catégorie est manquant.');
      return;
    }

    if (!token) {
      toast.error('Authentification requise. Veuillez vous connecter.');
      return;
    }

    setIsLoading(true);
    setErrors({}); // Clear errors before new submission

    const dataToSend = new FormData();
    dataToSend.append('title', formData.title);
    dataToSend.append('slug', formData.slug);

    if (formData.image) {
      dataToSend.append('image', formData.image);
    }
    else if (category?.image && imagePreview === '') {
      dataToSend.append('image_removed', 'true');
    }
    dataToSend.append('_method', 'PUT'); // Keep this if backend expects it via POST

    try {
      const result = await fetchData(`${baseUrlNormalized}/api/categories/${formData.id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: dataToSend,
      });

      toast.success(result?.message || 'Catégorie mise à jour avec succès !');

      if (result?.category?.image) { // Assuming backend returns updated category object with image property
        const updatedImageUrl = `${baseUrlNormalized}/storage/${result.category.image}`;
        setImagePreview(updatedImageUrl);
      } else if (result?.category?.image === null) {
        setImagePreview(''); // Backend confirmed image removal
      }

      setErrors({}); // Clear all errors on success
    } catch (error) {
      try {
        const parsedError = JSON.parse(error.message);
        if (parsedError.errors) {
          setErrors(parsedError.errors);
          toast.error(parsedError.message || 'La validation a échoué. Veuillez vérifier le formulaire.');
        } else {
          toast.error(parsedError.message || 'Une erreur est survenue lors de la mise à jour de la catégorie.');
        }
      } catch (jsonError) {
        toast.error(error.message || 'Erreur réseau ou une erreur inattendue est survenue.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const removeImage = useCallback(() => {
    setImagePreview(''); 
    setFormData((prev) => ({ ...prev, image: null })); // Explicitly set image to null for submission
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Clear file input value
    }
    if (errors.image) {
      setErrors((prevErrors) => ({ ...prevErrors, image: undefined }));
    }
    toast.info("L'image a été supprimée. La catégorie sera mise à jour sans image si vous sauvegardez.");
  }, [errors.image]);


  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden font-sans">
      <form onSubmit={handleSubmit} className="p-8">
        <div className="mb-8">
          <label htmlFor="title" className="block font-semibold text-gray-900 mb-3 text-sm">
            Titre de la Catégorie <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleTitleChange}
            placeholder="Entrez le titre de la catégorie..."
            className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.title
                ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]'
            }`}
            aria-invalid={!!errors.title}
            aria-describedby={errors.title ? 'title-error' : undefined}
          />
          {errors.title && <p id="title-error" className="mt-2 text-red-600 text-sm font-medium">{errors.title}</p>}
        </div>

        <div className="mb-8">
          <label htmlFor="slug" className="block font-semibold text-gray-900 mb-3 text-sm">
            Slug d'URL <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-medium">
              /categories/
            </span>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleSlugChange}
              placeholder="votre-slug-de-categorie"
              className={`w-full pl-36 py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                errors.slug
                  ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                  : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]'
              }`}
              aria-invalid={!!errors.slug}
              aria-describedby={errors.slug ? 'slug-error' : undefined}
            />
          </div>
          {errors.slug && <p id="slug-error" className="mt-2 text-red-600 text-sm font-medium">{errors.slug}</p>}
          <p className="mt-2 text-gray-500 text-xs">
            Le slug est généré automatiquement à partir du titre, mais vous pouvez le personnaliser.
          </p>
        </div>

        <div className="mb-8">
          <label className="block font-semibold text-gray-900 mb-3 text-sm">Image de la Catégorie</label>

          {!imagePreview ? (
            <div
              className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
                errors.image ? 'border-red-300' : 'border-gray-300 hover:border-[var(--color-primary-500)] hover:bg-gray-50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png, image/jpeg, image/gif, image/webp"
                onChange={(e) => handleImageUpload(e.target.files?.[0])}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                aria-label="Charger une image de catégorie"
              />

              <div className="space-y-4 pointer-events-none">
                <div className="w-16 h-16 mx-auto bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-8 w-8 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Déposez votre image ici</p>
                  <p className="text-gray-500 text-xs">ou cliquez pour parcourir les fichiers</p>
                </div>
                <p className="text-xs text-gray-400">PNG, JPG, GIF, WebP jusqu'à 2 Mo</p>
              </div>
            </div>
          ) : (
            <div className="relative rounded-2xl overflow-hidden border-2 border-gray-200">
              <div className="aspect-video relative bg-gray-100">
                <img src={imagePreview} alt="Aperçu de la catégorie" fill className="object-cover w-full h-full" />
              </div>
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-white/90 backdrop-blur-sm text-gray-700 p-2 rounded-full shadow-lg hover:bg-white transition-all duration-200"
                  aria-label="Modifier l'image"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={removeImage}
                  className="bg-red-500/90 backdrop-blur-sm text-white p-2 rounded-full shadow-lg hover:bg-red-500 transition-all duration-200"
                  aria-label="Supprimer l'image"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png, image/jpeg, image/gif, image/webp"
                onChange={(e) => handleImageUpload(e.target.files?.[0])}
                className="hidden"
                aria-label="Sélectionner une nouvelle image"
              />
            </div>
          )}
          {errors.image && <p className="mt-2 text-red-600 text-xs font-medium">{errors.image}</p>}
        </div>

        <div className="flex flex-col  sm:flex-row gap-4 pt-6 border-t border-gray-200 mt-10">
          <Link to="/categories" className="block cursor-pointer text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
            <button
              type="button"
              disabled={isLoading}
              className='cursor-pointer'
            >
              Annuler  
            </button>
          </Link>  
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 cursor-pointer px-4 text-sm font-semibold rounded-2xl flex items-center justify-center gap-2 transition-all duration-200 ${
              isLoading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-[1.01]'
            }`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Mise à jour...
              </>
            ) : (
              <>
                Mettre à jour
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </>
            )}
          </button>
        </div>
      </form>

      {(formData.title || imagePreview) && (
        <div className="border-t border-gray-200 p-8 bg-gray-50 ">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Aperçu en direct</h3>
          <div className="bg-white rounded-2xl shadow-md w-80 overflow-hidden border border-gray-100 max-w-sm">
            <div className="aspect-video relative bg-gradient-to-br from-[var(--color-primary-50)] via-white to-gray-100">
              {imagePreview ? (
                <img src={imagePreview} alt="Aperçu de la catégorie" fill className="object-cover w-full h-full" />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <svg className="h-12 w-12 text-[var(--color-primary-100)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
              )}
              <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm text-[var(--color-primary-500)] px-2 py-1 rounded-full text-[10px] font-semibold">
                0 éléments
              </div>
            </div>
            <div className="p-4">
              <h4 className="font-bold text-gray-900 mb-2 text-sm">{formData.title || 'Titre de la Catégorie'}</h4>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}